import { useEffect, useState } from "react";
import { Download, FileCode2, Share2, ZoomIn } from "lucide-react";
import { apiClient } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { openPreview } from "../chat/PreviewWindow";
import { openDeploy } from "../deploy/DeployDialog";

const ICON_BUTTON = "inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50";

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// Imagen o diseño creado por el Orquestador, con íconos para descargar, ampliar y compartir. Se pide con la sesión del usuario (cabecera Authorization)
// y se muestra como imagen local; si el archivo ya expiró (24 h), se dice con claridad.
export function DesignPreviewSection({ section }) {
  const [state, setState] = useState({ status: "loading", url: "", blob: null });
  const [big, setBig] = useState(false); // imagen ampliada
  const [actual, setActual] = useState(false); // tamaño real (con desplazamiento)
  const [error, setError] = useState("");
  const filename = section.filename || "imagen.png";
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const extras = Array.isArray(section.extra_downloads) ? section.extra_downloads : [];

  useEffect(() => {
    let alive = true;
    let objectUrl = "";
    setState({ status: "loading", url: "", blob: null });
    apiClient
      .get(`/exports/${section.download_id}/download`, { responseType: "blob" })
      .then((res) => {
        objectUrl = window.URL.createObjectURL(res.data);
        if (alive) setState({ status: "ready", url: objectUrl, blob: res.data });
        else window.URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        if (alive) setState({ status: "error", url: "", blob: null });
      });
    return () => {
      alive = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [section.download_id]);

  const download = () => {
    setError("");
    if (state.blob) saveBlob(state.blob, filename);
  };

  const downloadExtra = async (extra) => {
    setError("");
    try {
      const res = await apiClient.get(`/exports/${extra.download_id}/download`, { responseType: "blob" });
      saveBlob(res.data, extra.filename || `archivo.${extra.format}`);
    } catch (err) {
      setError("No se pudo descargar el archivo (se guarda 24 horas).");
    }
  };

  const share = async () => {
    setError("");
    if (!state.blob) return;
    try {
      const file = new File([state.blob], filename, { type: state.blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: section.title || filename });
      } else {
        saveBlob(state.blob, filename); // este equipo no puede compartir archivos: se descarga
      }
    } catch (err) {
      if (err && err.name !== "AbortError") setError("No se pudo compartir. Descárgala y compártela desde tu galería.");
    }
  };

  return (
    <figure data-testid="design-preview" className="my-3 max-w-md">
      {state.status === "ready" && (
        <img
          data-testid="design-preview-image"
          src={state.url}
          alt={section.title || section.filename || "Diseño"}
          title="Pulsa para ampliar"
          onClick={() => setBig(true)}
          className="w-full rounded-lg border border-border bg-white cursor-zoom-in"
        />
      )}
      {state.status === "loading" && (
        <div data-testid="design-preview-loading" className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Cargando la vista previa…
        </div>
      )}
      {state.status === "error" && (
        <div data-testid="design-preview-error" className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          La vista previa ya no está disponible (los diseños se guardan 24 horas). Pídeme el diseño de nuevo.
        </div>
      )}
      {section.title && <figcaption className="mt-1 text-xs text-muted-foreground">{section.title}</figcaption>}
      {state.status === "ready" && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <button type="button" data-testid="design-preview-download" className={ICON_BUTTON} onClick={download} title="Descargar" aria-label="Descargar">
            <Download className="h-4 w-4" />
          </button>
          <button type="button" data-testid="design-preview-zoom" className={ICON_BUTTON} onClick={() => setBig(true)} title="Ampliar" aria-label="Ampliar">
            <ZoomIn className="h-4 w-4" />
          </button>
          {canShare && (
            <button type="button" data-testid="design-preview-share" className={ICON_BUTTON} onClick={share} title="Compartir" aria-label="Compartir">
              <Share2 className="h-4 w-4" />
            </button>
          )}
          {extras.map((extra, i) => (
            <button
              key={extra.download_id}
              type="button"
              data-testid={`design-preview-extra-${i}`}
              className={ICON_BUTTON}
              onClick={() => downloadExtra(extra)}
              title={`Descargar ${String(extra.format || "").toUpperCase()}${String(extra.format || "").toLowerCase() === "svg" ? " (editable)" : ""}`}
              aria-label={`Descargar ${String(extra.format || "").toUpperCase()}`}
            >
              <FileCode2 className="h-4 w-4" />
            </button>
          ))}
          {section.preview_token && (
            <Button data-testid="design-preview-open" size="sm" onClick={() => openPreview(section.preview_token, section.preview_title)}>
              ▶ Abrir vista previa
            </Button>
          )}
          {section.preview_token && (
            <Button data-testid="design-preview-deploy" size="sm" variant="outline" onClick={openDeploy}>
              🚀 Desplegar
            </Button>
          )}
        </div>
      )}
      {error && (
        <p data-testid="design-preview-action-error" className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
      {state.status === "ready" && (
        <Dialog open={big} onOpenChange={setBig}>
          <DialogContent data-testid="design-lightbox" className="max-w-[96vw] w-[96vw] max-h-[94vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{section.title || section.filename || "Imagen"}</DialogTitle>
            </DialogHeader>
            <div className="overflow-auto max-h-[72vh] rounded-lg border border-border bg-white">
              <img
                data-testid="design-lightbox-image"
                src={state.url}
                alt={section.title || section.filename || "Imagen"}
                onClick={() => setActual((v) => !v)}
                className={actual ? "max-w-none cursor-zoom-out" : "mx-auto max-w-full max-h-[70vh] cursor-zoom-in"}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <Button data-testid="design-lightbox-actual" size="sm" variant="outline" onClick={() => setActual((v) => !v)}>
                {actual ? "Ajustar a la ventana" : "Tamaño real"}
              </Button>
              <button type="button" data-testid="design-lightbox-download" className={ICON_BUTTON} onClick={download} title="Descargar" aria-label="Descargar">
                <Download className="h-4 w-4" />
              </button>
              {section.preview_token && (
                <Button data-testid="design-lightbox-open" size="sm" onClick={() => { setBig(false); openPreview(section.preview_token, section.preview_title); }}>
                  ▶ Abrir vista previa
                </Button>
              )}
              <Button data-testid="design-lightbox-close" size="sm" variant="outline" onClick={() => setBig(false)}>Cerrar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </figure>
  );
}
